import { Link } from 'react-router-dom'
import { faBars } from '@fortawesome/pro-light-svg-icons';
import { faBars as faBarsSolid } from '@fortawesome/pro-solid-svg-icons';
import { filterTabsByPermissions, useTabObjectLogic } from '../../utils/hooks/useTabLogic'
import { TabsInfo } from './types'
import './editcardtabs.sass'
import { calculateTopOfElement } from '../../utils/globals/components'
import { UserPermissions } from '../CreateRoutering/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import LoaderDecider from '../LoaderDecider'
import { joinAndRemoveDuplicatedBars } from '../CreateRoutering/functions'
import { WhiteBox } from '../AppLayout/WhiteBox'
import TwinIcon from '../TwinIcon'
import useOnClickOutside from '../../utils/hooks/useOnClickOut';
import { getSingleEmployeeParam } from '../../utils/reducers/getters';

interface EditCardTabsProps extends TabsInfo{
    basicData: { [key: string]: any } | null
    basePath: string
    userPermissions: UserPermissions
    getData: () => Promise<void>
    className?: string
    Wrapper?: React.FC<any>
}

const EditCardTabs: React.FC<EditCardTabsProps> = ({ tabs, tab, basicData, basePath, userPermissions, getData, className, Wrapper }) => {
    const tabsFiltered = useMemo(() => filterTabsByPermissions(tabs, userPermissions), [tabs, userPermissions])
    const { active, ComponentFromTab, skipWhiteBox } = useTabObjectLogic(tabsFiltered, tab)
    if (!tabsFiltered || !Object.keys(tabsFiltered).length) {
        return null
    }
    return (
        <div className={'flex edit_card_tabs flex-auto overflow-auto relative ' + (className || '')}>
            <EditCardTabsLeftBarLink tabs={tabsFiltered} tab={active} basePath={basePath}/>
            <EditCardTabsTab Wrapper={Wrapper} basicData={basicData} skipWhiteBox={skipWhiteBox}>
                <LoaderDecider loading={basicData ? false : true}>
                    <ComponentFromTab basicData={basicData} userPermissions={tabsFiltered[active].userPermission} getData={getData} extraProps={tabsFiltered[active].extraProps} />
                </LoaderDecider>
            </EditCardTabsTab>
        </div>
    )
}


interface EditCardTabsLeftBarProps extends TabsInfo {
    children?: React.ReactNode
}
const ROW_HEIGHT = 31
const GAP_BTW = 15
interface EditCardTabsLeftBarLinkProps extends EditCardTabsLeftBarProps {
    basePath: string
}

export const EditCardTabsLeftBarLink: React.FC<EditCardTabsLeftBarLinkProps> = ({ basePath, tabs, tab }) => {
    const { setOpened, showInBubble, opened, classNameFloatingParam } = useEditCardTabsLeftBarLogic({ignoreBubble: false})

    const renderThis: JSX.Element[] = []
    for (const key in tabs) {
        renderThis.push(<EditCardTabsLeftItem key={key} text={tabs[key].text} isActive={tab === key} to={key} basePath={basePath} onClick={showInBubble ? () => setOpened?.(false) : undefined} />)
    }

    if (showInBubble && opened) {
        return <EditCardTabsBubbleLeftBar tabs={tabs} tab={tab} className={classNameFloatingParam} setOpened={setOpened}>{renderThis}</EditCardTabsBubbleLeftBar>
    }

    return (
        opened ? <EditCardTabsLeftBar tabs={tabs} tab={tab} setOpened={setOpened}>{renderThis}</EditCardTabsLeftBar> : <EditCardTabsDefaultClosedIcon showInBubble={showInBubble} onClick={() => setOpened(true)}/>
    )
}                       

interface EditCardTabsLeftBarDivProps extends EditCardTabsLeftBarProps {
    onClick: (tabId: string) => void
    children?: React.ReactNode
}

export const EditCardTabsLeftBarDiv: React.FC<EditCardTabsLeftBarDivProps> = ({ onClick, tabs, tab, children }) => {
    const { setOpened, opened } = useEditCardTabsLeftBarLogic({ignoreBubble: true})
    
    const renderThis: JSX.Element[] = []
    for (const key in tabs) {
        renderThis.push(<EditCardTabsLeftDivItem key={key} text={tabs[key].text} isActive={tab === key} onClick={() => onClick(key)} />)
    }
    if (!opened) {
        return <EditCardTabsLeftBarClosed onClick={() => setOpened(true)} />
    }
    return (
        <EditCardTabsLeftBar setOpened={() => setOpened(false)} tabs={tabs} tab={tab} >
            {renderThis} {children}
        </EditCardTabsLeftBar>
    )
}

interface EditCardTabsLeftBarInnerProps extends EditCardTabsLeftBarProps {
    setOpened?: React.Dispatch<React.SetStateAction<boolean>> | null
    showInBubble?: boolean
    className?: string
    myRef?: any
}

const EditCardTabsBubbleLeftBar: React.FC<EditCardTabsLeftBarInnerProps> = ({ className, setOpened, ...props }) => {
    const { ref } = useEditCardTabsBubbleLeftBarProps({setOpened})
    return (
        <div className='fixed w-full h-full bg-black bg-opacity-20 z-10 bubble_left_bar_bg'>
            <EditCardTabsLeftBar {...props} setOpened={() => setOpened?.(false)} myRef={ref} className={className} />
        </div >
    )
}

interface EditCardTabsBubbleLeftBarPropsProps {
    setOpened?: React.Dispatch<React.SetStateAction<boolean>> | null
}

const useEditCardTabsBubbleLeftBarProps = ({setOpened}: EditCardTabsBubbleLeftBarPropsProps) => {
    const ref = useRef<any>(null)

    useOnClickOutside(ref, () => {
        setOpened?.(false)
    })
    
    return {ref}
}


const EditCardTabsLeftBar: React.FC<EditCardTabsLeftBarInnerProps> = ({ tabs, tab, children, setOpened, className, myRef }) => {
    return (
        <div ref={myRef} className={'editcardtabs_leftbar relative  flex flex-auto ' + (className || '')}>
            <TwinIcon icon={faBars} size='lg' className='cursor-pointer text-blac h-19 opacity-50 editcardtabs_leftbar_openedicon' onClick={() => setOpened?.(false)} />
            <div className='overflow-auto flex-auto flex'>
                <div className='ectlb_bar'>
                    <div className='ectlb_bar_bar' style={{top: calculateTopOfElement(ROW_HEIGHT, GAP_BTW, Object.keys(tabs).indexOf(String(tab)) ), height: ROW_HEIGHT}}></div>
                </div>
                <div className='ectlb_tabs flex-auto w-1'>
                    {children}
                </div>
            </div>
        </div>
    )
}

interface EditCardTabsLeftBarLogicProps {
    ignoreBubble?: boolean
}

const useEditCardTabsLeftBarLogic = ({ ignoreBubble }: EditCardTabsLeftBarLogicProps) => {
    const showBubbleInnerTabs = useMemo(() => getSingleEmployeeParam('showBubbleInnerTabs'), [])
    const [opened, setOpened] = useState<boolean>(ignoreBubble ? true : showBubbleInnerTabs !== 'on')
    const [showInBubble, setShowInBubble] = useState<boolean>(showBubbleInnerTabs === 'on')
    
    const resizeLeftMenu = useCallback(() => {
        if (!ignoreBubble) {
            setShowInBubble(window.innerWidth > 700 ? showBubbleInnerTabs === 'on' : true)
            setOpened((old) => window.innerWidth > 700 ? old : false)
        }
    }, [ignoreBubble, showBubbleInnerTabs])

    useEffect(() => {
        resizeLeftMenu()
        window.addEventListener('resize', resizeLeftMenu)
        return () => {
            window.removeEventListener('resize', resizeLeftMenu)
        }
    }, [resizeLeftMenu])

    const classNameFloatingParam = showInBubble ? 'floating_editcardtabs_leftbar' : ''

    return { opened, setOpened, showInBubble, classNameFloatingParam }
}

interface EditCardTabsTabProps {
    children: React.ReactNode
    Wrapper?: React.FC<any>
    basicData: { [key: string]: any } | null
    skipWhiteBox?: boolean
}

const EditCardTabsTab: React.FC<EditCardTabsTabProps> = ({ children, Wrapper, basicData, skipWhiteBox }) => {
    let childrenDiv = (
        <WhiteBox className='editcardtabs_tab'>
            {children}
        </WhiteBox>
    )

    if (skipWhiteBox) {
        childrenDiv = (
            <div className='editcardtabs_tab skip_white_box'>
                {children}
            </div>
        )
    }

    if (Wrapper) {
        return (
            <Wrapper basicData={basicData}>
                {childrenDiv}
            </Wrapper>
        )
    }
    return childrenDiv
}

interface EditCardTabsLeftItemProps {
    text: string | JSX.Element
    to: string
    isActive?: boolean
    basePath: string
    onClick?: () => void 
}

const EditCardTabsLeftItem: React.FC<EditCardTabsLeftItemProps> = ({basePath, text, to,  isActive, onClick}) => {
    return (
        <Link to={joinAndRemoveDuplicatedBars(basePath, '/' + to)} onClick={onClick} className={'editcardtabs_tabitem regular18 ' + (isActive ? 'active' : '')}>
            <span className='twin_elipsis'>
                {text}
            </span>
        </Link>
    )
}
interface EditCardTabsLeftDivItemProps {
    text: string | JSX.Element
    isActive?: boolean
    onClick: () => void
}
const EditCardTabsLeftDivItem: React.FC<EditCardTabsLeftDivItemProps> = ({text, isActive, onClick}) => {
    return (
        <div className={'editcardtabs_tabitem regular18 cursor-pointer ' + (isActive ? 'active' : '')} onClick={onClick}>
            <span className='twin_elipsis'>{text}</span>
        </div>
    )
}
interface EditCardTabsHorizontalWithoutLinksProps extends EditCardTabsLeftBarProps {
    onClick: (tabId: string) => void
    children?: React.ReactNode
    extraComponentData?: { [key: string]: any }
    className?: string
}
export const EditCardTabsHorizontalWithoutLinks: React.FC<EditCardTabsHorizontalWithoutLinksProps> = ({ onClick, tabs, tab, extraComponentData, className }) => {
    const { active, ComponentFromTab } = useTabObjectLogic(tabs, tab)
    const renderThis: JSX.Element[] = []
    for (const key in tabs) {
        renderThis.push(<EditCardTabsHorizontalWithoutLinksItem key={key} text={tabs[key].text} isActive={active === key} onClick={() => onClick(key)} />)
    }
    return (
        <div className='horizontal_tabs_parent flex-auto flex-col flex'>
            <div className={'flex-auto flex flex-col horizontal_tabs ' + (className || '')}>
                <div className='horizontal_tabs_subtitle text-18'>
                    {renderThis}
                </div>
                <div className='flex-auto h-1 flex flex-col'>
                    <ComponentFromTab {...extraComponentData} />
                </div>
            </div>
        </div>
    )
}
interface EditCardTabsHorizontalWithoutLinksItemProps {
    text: string | JSX.Element
    isActive?: boolean
    onClick: () => void
}
const EditCardTabsHorizontalWithoutLinksItem: React.FC<EditCardTabsHorizontalWithoutLinksItemProps> = ({text, isActive, onClick}) => {
    return (
        <div className={'flex justify-center p-10 cursor-pointer horizontal_tab_item_without_link horizontal_tab_item ' + (isActive ? 'active' : '')} onClick={onClick}>
            <span className='twin_elipsis'>{text}</span>
        </div>
    )
}

interface EditCardTabsHorizontalTabsProps extends TabsInfo {
    basePath: string
}

export const EditCardTabsHorizontalTabs: React.FC<EditCardTabsHorizontalTabsProps> = ({ basePath, tabs, tab }) => {
    const { ComponentFromTab } = useTabObjectLogic(tabs, tab)
    const renderThis: JSX.Element[] = []
    for (const key in tabs) {
        renderThis.push(<EditCardTabsHorizontalItem key={key} text={tabs[key].text} isActive={tab === key} to={key} basePath={basePath} />)
    }
    return (
        <div className='horizontal_tabs_parent flex-auto flex-col flex'>
            <div className={'flex-auto flex flex-col horizontal_tabs '}>
                <div className='horizontal_tabs_subtitle light18'>
                    {renderThis}
                </div>
                <div className='flex-auto h-1 flex flex-col horizontal_tabs_content'>
                    <ComponentFromTab />
                </div>
            </div>
        </div>
    )
}
interface EditCardTabsHorizontalItemProps {
    text: string | JSX.Element
    to: string
    isActive?: boolean
    basePath: string
}
const EditCardTabsHorizontalItem: React.FC<EditCardTabsHorizontalItemProps> = ({ basePath, text, to, isActive }) => {
    return (
        <Link to={joinAndRemoveDuplicatedBars(basePath, '/' + to)} className={'mr-20 px-10 pb-20 cursor-pointer horizontal_tab_item ' + (isActive ? 'active' : '')}>
            {text}
        </Link>
    )
}
interface EditCardTabsLeftBarIconDefaultProps extends EditCardTabsLeftBarIconProps {
    showInBubble: boolean
}
const EditCardTabsDefaultClosedIcon: React.FC<EditCardTabsLeftBarIconDefaultProps> = ({ showInBubble, ...props }) => {
    return showInBubble ? <EditCardTabsLeftBubbleClosedIcon {...props} /> : <EditCardTabsLeftBarClosed {...props} />
}

interface EditCardTabsLeftBarIconProps {
    onClick?: () => void
}

const EditCardTabsLeftBubbleClosedIcon: React.FC<EditCardTabsLeftBarIconProps> = ({ onClick }) => {
    return (
        <div className='editcardtabs_leftbar_floating_closed z-1 rounded-full flex items-center justify-center absolute cursor-pointer' onClick={onClick}>
            <TwinIcon icon={faBarsSolid} size='lg' className='text-white ' />
        </div>
    )
}

const EditCardTabsLeftBarClosed: React.FC<EditCardTabsLeftBarIconProps> = ({ onClick }) => {
    return (
        <div className='editcardtabs_leftbar_closed py-20 flex justify-center'>
            <TwinIcon icon={faBars} size='lg' className='cursor-pointer text-black opacity-50' onClick={onClick} />
        </div>
    )
}

export default EditCardTabs